import { useEffect, useState } from 'react'
import { Button, Icon, LoadingState, toastError, toastSuccess } from 'admination-react'
import { SignaturePad } from '@/core/components/ui/signature-pad'
import {
  loadMemberSignatur,
  loadStempel,
  saveMemberSignatur,
  saveStempel,
  signingAssetDataUrl,
  type SigningAsset,
} from '@/core/data/signingAssets'
import { dataUrlToBlob } from '@/core/lib/files'
import { SIGNATURE_ACCEPT } from '@/core/lib/fileTypes'
import { DropCard } from '@/core/components/modal/DropCard'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'

type SlotKind = 'org-stamp' | 'member-signature'

function SigningAssetSlot({
  kind,
  ownerId,
  label,
  hint,
}: {
  kind: SlotKind
  ownerId: string
  label: string
  hint: string

}) {
  const drawable = kind === 'member-signature'
  const [asset, setAsset] = useState<SigningAsset | null | 'error' | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [padOpen, setPadOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    const read = kind === 'org-stamp' ? loadStempel : loadMemberSignatur
    read(ownerId)
      .then((a) => {
        if (!cancelled) setAsset(a ?? null)
      })
      .catch((e) => {

        console.error(`Signing-Asset (${kind}) laden fehlgeschlagen:`, e)
        if (!cancelled) setAsset('error')
      })
    return () => {
      cancelled = true
    }
  }, [kind, ownerId])

  async function store(blob: Blob, { notify = true }: { notify?: boolean } = {}) {
    const identity = captureWriteIdentity()
    setBusy(true)
    try {
      const write = kind === 'org-stamp' ? saveStempel : saveMemberSignatur
      const saved = await write(ownerId, blob)
      if (!isWriteIdentityCurrent(identity)) return
      setAsset(saved)
      if (notify) toastSuccess(`${label} gespeichert.`)
    } catch (e) {
      console.error(`Signing-Asset (${kind}) speichern fehlgeschlagen:`, e)
      if (isWriteIdentityCurrent(identity)) toastError(friendlyErrorMessage(e, "Speichern fehlgeschlagen."))
      throw e
    } finally {
      if (isWriteIdentityCurrent(identity)) setBusy(false)
    }
  }

  return (
    <div className="field">

      <div className="sign-preview">
        {asset === undefined ? (
          <LoadingState>{label}{" wird geladen …"}</LoadingState>
        ) : asset === 'error' ? (
          <span role="alert">{label}{" konnte nicht geladen werden."}</span>
        ) : asset === null ? (
          <span>Noch nicht hinterlegt</span>
        ) : (
          <img src={signingAssetDataUrl(asset)} alt={label} />
        )}
      </div>
      <DropCard title={`${label} als JPG oder PNG auswählen oder hierher ziehen`} body={hint}
        accept={SIGNATURE_ACCEPT} onFile={(file) => void store(file).catch(() => {})}
        loading={busy} loadingLabel={`${label} wird gespeichert …`} disabled={asset === undefined} />
      <div className="section-actions">
        {drawable && (
          <Button type="button" disabled={busy} onClick={() => setPadOpen(true)}>
            <Icon name="i-edit" />
            Zeichnen</Button>
        )}
      </div>
      {drawable && (

        <SignaturePad
          open={padOpen}
          title={label}
          onSubmit={(pngDataUrl) => store(dataUrlToBlob(pngDataUrl), { notify: false })}
          onClose={() => setPadOpen(false)}
        />
      )}
    </div>
  )
}

export function OrgStempelSection({ organisationId }: { organisationId: string }) {
  return (
    <div className="section">
      <h3 className="section-header">Stempel</h3>
      <div className="section-body">
        <SigningAssetSlot
          kind="org-stamp"
          ownerId={organisationId}
          label="Stempel"
          hint="Firmenstempel - wird auf der Trägerbestätigung gedruckt. JPG oder PNG."
        />
      </div>
    </div>
  )
}

export function MySignatureSection({ memberId }: { memberId: string }) {
  return (
    <div className="section">
      <h3 className="section-header">Meine Unterschrift</h3>
      <div className="section-body">
        <SigningAssetSlot
          kind="member-signature"
          ownerId={memberId}
          label="Unterschrift"
          hint="Wird überall gedruckt, wo Ihre Unterschrift gebraucht wird: auf Erstgespräch, Vertrag und Trägerbestätigung als handelnde Person, auf Coachingnachweisen als Coach. Sie gehört zu Ihrer Person und gilt für alle Organisationen, in denen Sie Mitglied sind. JPG oder PNG, am besten freigestellt."
        />
      </div>
    </div>
  )
}
